import { API_CONFIG, getApiUrl } from "../config/api";
import {
  FormImagePointer,
  FormTemplateField,
  ReadFormUploadItemRequest,
  ReadFormUploadUrlsResponse,
  RequestUploadPayload,
  UploadUrlResponse,
} from "../types/formTemplates";
import { activeImageEntries, readPointerS3Key } from "./formImagePointer";

type FetchWithAuth = (url: string, options?: RequestInit) => Promise<unknown>;

/** Backend accepts at most 20 items per read request. */
export const FORM_UPLOAD_READ_BATCH_MAX = 20;

function isPhiS3Key(value: unknown): value is string {
  return typeof value === "string" && value.startsWith("phi/");
}

/**
 * Collect { field_id, s3_key } pairs for batch presigned GET (signature +
 * image_upload only). Handles all three image_upload shapes (legacy
 * bare string, multi-image array of strings, post-dual-write pointer
 * dicts) and skips tombstoned pointer entries.
 */
export function collectPhiReadItems(
  fields: FormTemplateField[],
  data: Record<string, unknown>,
): ReadFormUploadItemRequest[] {
  const items: ReadFormUploadItemRequest[] = [];
  for (const field of fields) {
    if (field.type !== "signature" && field.type !== "image_upload") continue;
    const v = data[field.id];
    const keys: string[] = [];
    if (field.type === "signature") {
      if (isPhiS3Key(v)) keys.push(v);
    } else {
      for (const entry of activeImageEntries(
        v as Parameters<typeof activeImageEntries>[0],
      )) {
        const key = readPointerS3Key(entry);
        if (isPhiS3Key(key)) keys.push(key as string);
      }
    }
    for (const s3_key of keys) {
      items.push({ field_id: field.id, s3_key });
    }
  }
  return items;
}

export type ResolvedUploadDisplayUrl = {
  url: string;
  expiresInSeconds: number;
  expiresAtMs: number;
};

/**
 * Resolve stored phi/ keys to presigned GET URLs (batch, chunked by 20).
 * Prefers matching response rows to requests by `s3_key` when the backend
 * echoes it; otherwise falls back to positional matching with a field_id
 * sanity check.
 */
export async function readFormUploadDisplayUrls(
  fetchWithAuth: FetchWithAuth,
  salonId: string,
  clientId: string,
  formId: string,
  items: ReadFormUploadItemRequest[],
): Promise<Map<string, ResolvedUploadDisplayUrl>> {
  const out = new Map<string, ResolvedUploadDisplayUrl>();
  if (items.length === 0) return out;

  const now = () => Date.now();

  for (let i = 0; i < items.length; i += FORM_UPLOAD_READ_BATCH_MAX) {
    const batch = items.slice(i, i + FORM_UPLOAD_READ_BATCH_MAX);
    const res = (await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_UPLOADS_READ, "", {
        salonId,
        clientId,
        formId,
      }),
      { method: "POST", body: JSON.stringify({ items: batch }) },
    )) as ReadFormUploadUrlsResponse;

    const rows = res.items ?? [];
    const t0 = now();
    const rowByKey = new Map<string, (typeof rows)[number]>();
    for (const r of rows) {
      if (r?.s3_key) rowByKey.set(r.s3_key, r);
    }
    for (let j = 0; j < batch.length; j += 1) {
      const req = batch[j];
      const row = rowByKey.get(req.s3_key) ?? rows[j];
      if (!row?.url || row.field_id !== req.field_id) {
        throw new Error(
          "Unexpected uploads/read response shape (missing URL or field mismatch).",
        );
      }
      const expiresInSeconds = Math.max(
        1,
        Math.floor(Number(row.expires_in_seconds) || 600),
      );
      out.set(req.s3_key, {
        url: row.url,
        expiresInSeconds,
        expiresAtMs: t0 + expiresInSeconds * 1000,
      });
    }
  }

  return out;
}

/**
 * Two-step upload for signature + image_upload fields:
 *
 *   1. POST /.../forms/{form_id}/uploads → returns a presigned URL, s3_key,
 *      and the required headers the S3 PUT needs. The backend validated
 *      field_id is a real upload field in the form's schema_snapshot.
 *   2. PUT the file binary to the presigned URL with EXACTLY the headers
 *      it handed back (extra headers break the signature).
 *
 * The s3_key is returned for the caller to stuff into `data[field_id]`
 * via the normal save-draft.
 */
export async function uploadFieldValue({
  fetchWithAuth,
  salonId,
  clientId,
  formId,
  fieldId,
  file,
  asPointer = false,
}: {
  fetchWithAuth: FetchWithAuth;
  salonId: string;
  clientId: string;
  formId: string;
  fieldId: string;
  file: File;
  /**
   * When true, return a `FormImagePointer` object instead of a bare
   * s3_key string. The pointer carries content_type / file_size /
   * filename / uploaded_at — captured at upload time so the backend
   * dual-write doesn't have to S3 HEAD them, and so the Documents tab
   * Info drawer can render without extra API calls.
   *
   * Defaults to false to keep signature uploads (which always want the
   * scalar key) and any direct legacy callers source-compatible.
   */
  asPointer?: boolean;
}): Promise<string | FormImagePointer> {
  const requestBody: RequestUploadPayload = {
    field_id: fieldId,
    content_type: file.type || "application/octet-stream",
    filename: file.name || undefined,
  };

  const presigned = (await fetchWithAuth(
    getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_FORM_UPLOADS, "", {
      salonId,
      clientId,
      formId,
    }),
    { method: "POST", body: JSON.stringify(requestBody) },
  )) as UploadUrlResponse;

  if (file.size > presigned.max_bytes) {
    const mb = (presigned.max_bytes / (1024 * 1024)).toFixed(0);
    throw new Error(`File exceeds ${mb} MB maximum upload size.`);
  }

  const requiredHeaders =
    presigned.required_headers && typeof presigned.required_headers === "object"
      ? Object.fromEntries(
          Object.entries(presigned.required_headers).filter(
            ([, v]) => v != null && String(v).length > 0,
          ),
        )
      : {};

  // Plain fetch — bypasses the authenticated helper because S3 signs the
  // request and rejects an extra Authorization header.
  // Use ArrayBuffer as body so the runtime does not attach File/Blob
  // Content-Type in addition to the signed headers (common cause of
  // SignatureDoesNotMatch on presigned PUT).
  const putBody = await file.arrayBuffer();
  const s3Response = await fetch(presigned.url, {
    method: "PUT",
    mode: "cors",
    credentials: "omit",
    cache: "no-store",
    headers: requiredHeaders,
    body: putBody,
  });

  if (!s3Response.ok) {
    let detail = "";
    try {
      detail = (await s3Response.text()).trim().slice(0, 500);
    } catch {
      /* ignore */
    }
    throw new Error(
      detail
        ? `Upload failed (${s3Response.status}): ${detail}`
        : `Upload failed (${s3Response.status}). Please try again.`,
    );
  }

  if (asPointer) {
    const pointer: FormImagePointer = {
      s3_key: presigned.s3_key,
      content_type: file.type || "application/octet-stream",
      file_size: file.size,
      filename: file.name || undefined,
      uploaded_at: new Date().toISOString(),
    };
    return pointer;
  }
  return presigned.s3_key;
}

/**
 * Convert a signature canvas dataURL (base64 PNG) into a File so the
 * upload flow treats it the same as any image_upload file.
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(",");
  const mimeMatch = header.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}
